import { FC, useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Menu,
  ActionIcon,
  Anchor,
  Paper,
  Tooltip,
} from '@mantine/core';
import {
  IconEdit,
  IconDots,
  IconTrash,
  IconDownload,
  IconExternalLink,
} from '@tabler/icons-react';
import { DiagnosticReport, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { PDFViewerModal } from '@/components/PDFViewerModal/PDFViewerModal';
import {
  downloadFile,
  getReportResultLabel,
  getReportResultValueFromCode,
} from '../utils/report-files.utils';
import { REPORT_MODALITY_OPTIONS } from '../utils/report-files.consts';
import { useDateDisplay } from '@/hooks/useDateDisplay';
import { calculateAgeAtGivenTime } from '@/utils/calculate-age-at-given-time';
import { LabeledField } from '@/components/LabeledField';
import {
  REPORT_MODALITY_EXTENSION_URL,
  PRENATAL_CHECK_REASON_EXTENSION_URL,
  PRENATAL_CHECK_TEST_TYPE_EXTENSION_URL,
} from '@/consts/systems.consts';

interface ReportCardProps {
  report: DiagnosticReport;
  patient: Patient;
  onEdit: (report: DiagnosticReport) => void;
  onDelete: (report: DiagnosticReport) => void;
  readonly?: boolean;
}

const RESULT_BADGE_COLORS: Record<string, string> = {
  NORMAL: 'teal',
  ABNORMAL: 'red',
  NON_SPECIFIC: 'yellow',
};

export const ReportCard: FC<ReportCardProps> = ({
  report,
  patient,
  onEdit,
  onDelete,
  readonly = false,
}) => {
  const { t } = useTranslation(['patient-pages', 'files-tab']);
  const medplum = useMedplum();
  const formatDate = useDateDisplay();
  const [viewerOpened, setViewerOpened] = useState(false);

  const resultValue = getReportResultValueFromCode(
    report.conclusionCode?.[0]?.coding?.[0]?.code
  );
  const resultLabel = getReportResultLabel(resultValue);
  const resultBadgeColor =
    (resultValue && RESULT_BADGE_COLORS[resultValue]) || 'gray';

  const examDate = report.effectiveDateTime
    ? formatDate(report.effectiveDateTime)
    : t('reportCards.noDate', { defaultValue: 'No date' });
  const comments = report.conclusion;
  const attachmentTitle = report.presentedForm?.[0]?.title;

  const getExamTitle = () =>
    report.code?.text ||
    report.code?.coding?.[0]?.display ||
    t('reportCards.unknownExam', { defaultValue: 'Unknown exam' });

  const calculateAgeLabel = (): string | null => {
    if (!patient.birthDate || !report.effectiveDateTime) {
      return null;
    }

    try {
      const birthDate = new Date(patient.birthDate);
      const examDate = new Date(report.effectiveDateTime);
      const age = calculateAgeAtGivenTime(birthDate, examDate);

      const parts: string[] = [];
      if (age.years > 0) {
        parts.push(
          t('reportCards.ageYears', {
            defaultValue: '{{count}} years',
            count: age.years,
          })
        );
      }
      if (age.months > 0) {
        parts.push(
          t('reportCards.ageMonths', {
            defaultValue: '{{count}} months',
            count: age.months,
          })
        );
      }

      return parts.length > 0 ? parts.join(' ') : null;
    } catch {
      return null;
    }
  };

  const ageLabel = calculateAgeLabel();

  // Extract modality from extension
  const modalityCode = report.extension?.find(
    (ext) => ext.url === REPORT_MODALITY_EXTENSION_URL
  )?.valueCode;

  const getModalityLabel = (code: string | undefined): string | null => {
    if (!code) {
      return null;
    }
    // Search through all modality options to find the label
    const allModalities = [
      ...REPORT_MODALITY_OPTIONS.BRAIN_OR_ABDOMEN,
      ...REPORT_MODALITY_OPTIONS.THORAX,
    ];
    return allModalities.find((m) => m.value === code)?.label || code;
  };

  const modalityLabel = getModalityLabel(modalityCode);

  // Extract reason for test and test type from extensions
  const reasonForTest = report.extension?.find(
    (ext) => ext.url === PRENATAL_CHECK_REASON_EXTENSION_URL
  )?.valueString;
  const testType = report.extension?.find(
    (ext) => ext.url === PRENATAL_CHECK_TEST_TYPE_EXTENSION_URL
  )?.valueString;

  const attachmentUrl = report.presentedForm?.[0]?.url;

  const handleOpenInApp = (): void => {
    if (attachmentUrl) {
      setViewerOpened(true);
    }
  };

  const handleDownload = (): void => {
    if (!attachmentUrl) return;
    downloadFile({
      medplum,
      url: attachmentUrl,
      fileName: attachmentTitle || 'document.pdf',
      t: t as unknown as TFunction<'files-tab'>,
    });
  };

  const handleOpenInNewTab = (): void => {
    if (attachmentUrl) {
      window.open(attachmentUrl, '_blank');
    }
  };

  return (
    <Paper withBorder radius="md" p="sm">
      <Stack gap={0}>
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <Text fw={600} size="lg">
              {getExamTitle()}
            </Text>
            {attachmentTitle && (
              <Group gap={4}>
                <Anchor
                  onClick={handleOpenInApp}
                  style={{ cursor: 'pointer' }}
                  underline="hover"
                >
                  {attachmentTitle}
                </Anchor>
                <Tooltip
                  label={t('reportCards.download', {
                    defaultValue: 'Download',
                  })}
                >
                  <ActionIcon
                    variant="subtle"
                    size="xs"
                    onClick={handleDownload}
                    aria-label={t('reportCards.download', {
                      defaultValue: 'Download',
                    })}
                  >
                    <IconDownload size={14} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip
                  label={t('reportCards.openInNewTab', {
                    defaultValue: 'Open in new tab',
                  })}
                >
                  <ActionIcon
                    variant="subtle"
                    size="xs"
                    onClick={handleOpenInNewTab}
                    aria-label={t('reportCards.openInNewTab', {
                      defaultValue: 'Open in new tab',
                    })}
                  >
                    <IconExternalLink size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            )}
          </Group>
          <Group gap="xs">
            {resultLabel && (
              <Badge color={resultBadgeColor} variant="light">
                {t(`reportCards.resultOptions.${resultLabel}`, {
                  ns: 'patient-pages',
                  defaultValue: resultLabel,
                })}
              </Badge>
            )}
            {!readonly && (
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon variant="subtle">
                    <IconDots size={16} color="gray" />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconEdit size={16} />}
                    onClick={() => onEdit(report)}
                  >
                    {t('reportCards.edit', { defaultValue: 'Edit' })}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconTrash size={16} />}
                    color="red"
                    onClick={() => onDelete(report)}
                  >
                    {t('reportCards.delete', { defaultValue: 'Delete' })}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Group>

        <Group gap="xs" wrap="wrap">
          <LabeledField
            label={t('reportCards.examDateLabel', {
              defaultValue: 'Exam date',
            })}
            value={examDate}
          />
          {modalityLabel && (
            <Badge variant="light" color="blue">
              {modalityLabel}
            </Badge>
          )}
          {ageLabel && (
            <Badge variant="light" color="gray">
              {t('reportCards.ageBadge', {
                defaultValue: 'Age at exam: {{age}}',
                age: ageLabel,
              })}
            </Badge>
          )}
        </Group>

        {(reasonForTest || testType) && (
          <Group gap="xs" wrap="wrap" grow>
            {testType && (
              <LabeledField
                label={t('reportCards.testTypeLabel', {
                  defaultValue: 'Test type',
                })}
                value={testType}
              />
            )}
            {reasonForTest && (
              <LabeledField
                label={t('reportCards.reasonForTestLabel', {
                  defaultValue: 'Reason for test',
                })}
                value={reasonForTest}
              />
            )}
          </Group>
        )}

        {comments && (
          <LabeledField
            label={t('reportCards.commentsLabel', {
              defaultValue: 'Comments',
            })}
            value={comments}
          />
        )}
      </Stack>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        opened={viewerOpened}
        onClose={() => setViewerOpened(false)}
        url={attachmentUrl || null}
        title={attachmentTitle}
      />
    </Paper>
  );
};
